import {
  faCalendar,
  faDatabase,
  faGrid,
  faGearApi,
  faLandmarkDome,
  faLayer,
  faHeadSideGear,
  faRectangleVerticalHistory,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";

import { isUserAdmin } from "../../../common/constants/userRole";
import type { Module, ModulesDTO, SubModule } from "../../../common/types/ModulesDTO";
import type { UserDTO } from "../../../common/types/UserDTO";
import type { NavigationLink, SubLink } from "../../types";
import tokenManager, { TOKEN_KEY } from "../tokenManager";
import { getFromParams } from "../url";

const constructDashboardUrl = (
  baseUrl: string,
  user: UserDTO,
  allModuleAccess: boolean,
  accountId?: number,
  token?: string | null,
) => {
  const { email, accountName, id } = user;
  const isAdmin = isUserAdmin(user.roles);
  const selectedVenueId = user.properties?.selectedVenueId;
  // TODO: Calculate this by feature IS_PAID when it exists
  const isPaidVersion = Boolean(allModuleAccess);

  return `${baseUrl}/dashboard?headless=true&email=${encodeURIComponent(
    email,
  )}&accountId=${accountId}&accountName=${encodeURIComponent(
    accountName || "",
  )}&userId=${id}&venueId=${selectedVenueId}&isPaidVersion=${isPaidVersion}&isAdmin=${isAdmin}&token=${token}`;
};

const buildIframeUrl = (
  item: Module | SubModule,
  tokenParam: string,
  venueContext: string,
): string => {
  if (item.isVaadin) {
    const [baseUrl, vaadinPath] = item.url.split("#!");
    return `${baseUrl}?${tokenParam}#!${vaadinPath}`;
  }
  if (item.includeVenueId) {
    return `${item.url}?${venueContext}&${tokenParam}`;
  }
  return `${item.url}?${tokenParam}`;
};

const buildSubLinks = (
  to: string,
  tokenParam: string,
  contextParams: string,
  subModules?: SubModule[],
): SubLink[] | undefined => {
  if (!subModules) return undefined;
  if (!subModules.length) return undefined;
  return subModules.map((sm) => ({
    title: sm.name,
    to: `${to}/${sm.id}`,
    iFrameUrl: buildIframeUrl(sm, tokenParam, contextParams),
    path: sm.path,
    showInNav: sm.showInNav,
  }));
};

const MODULE_CONFIG: {
  key: keyof ModulesDTO;
  icon: IconDefinition;
  title: string;
  route: string;
}[] = [
  { key: "ask", icon: faHeadSideGear, title: "Ask", route: "ask" },
  { key: "dashboard", icon: faGrid, title: "Dashboard", route: "dashboard" },
  { key: "almanac", icon: faCalendar, title: "Almanac", route: "almanac" },
  { key: "platform", icon: faLayer, title: "Platform", route: "data/platform" },
  {
    key: "dataExplorer",
    icon: faDatabase,
    title: "Explorer",
    route: "data/data-explorer",
  },
  { key: "dexibitApi", icon: faGearApi, title: "API (Dexibit)", route: "data/api" },
  { key: "legacy", icon: faRectangleVerticalHistory, title: "Legacy", route: "legacy" },
  {
    key: "administration",
    icon: faLandmarkDome,
    title: "Admin",
    route: "administration",
  },
];

export const constructLinks = (
  moduleData?: ModulesDTO,
  user?: UserDTO,
  accountId?: number,
  allModuleAccess?: boolean,
  rootPath?: string,
): NavigationLink[] => {
  const token = getFromParams(TOKEN_KEY) || tokenManager.getToken();
  const selectedVenueId = user?.properties?.selectedVenueId;

  const accessTokenParam = `access_token=${token}`;
  const contextParams = `headless=true&venueId=${selectedVenueId}`;

  if (!moduleData || !user) {
    return [];
  }

  return MODULE_CONFIG.reduce<NavigationLink[]>((links, { key, icon, title, route }) => {
    const module = moduleData[key];
    if (!module) return links;

    const to = `${rootPath}/${route}`;
    const iFrameUrl =
      key === "dashboard"
        ? constructDashboardUrl(
            module.url,
            user,
            Boolean(allModuleAccess),
            accountId,
            token,
          )
        : buildIframeUrl(module, accessTokenParam, contextParams);

    links.push({
      to,
      title,
      fullTitle: module.name,
      icon,
      iFrameUrl,
      subLinks: buildSubLinks(to, accessTokenParam, contextParams, module.subModules),
    });
    return links;
  }, []);
};
